import React, {useCallback} from 'react'
import {FormattedDate} from 'react-intl'
import {useDispatch, useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {differenceInDays} from 'date-fns/differenceInDays'
import {parseISO} from 'date-fns/parseISO'
import useShowOverlay, {
  useShowHotelDatePickerOverlay
} from 'hooks/useShowOverlay/useShowOverlay'
import {DateGuestPickerSource, OverlayType} from 'modules/overlay/types'
import {
  getIsMultiRoomBundlesSearch,
  getIsNoDatesSearch
} from 'modules/search/selectors'
import {getSearchBoxValues} from 'modules/searchBox/selectors'
import {dateStringToMiddayDate} from 'utils'

import {ButtonGroup} from '@vio/atlas/ButtonGroup'
import {Card} from '@vio/atlas/Card/Card'
import {InputButton} from '@vio/atlas/helpers/InputButton'
import {Icon} from '@vio/atlas/Icon'
import {SkeletonLoader} from '@vio/atlas/SkeletonLoader'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {
  useDispatchTeamEvent,
  useTrackingTeam
} from '@vio/core/src/analytics/components/TeamContext'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action, Category, Entity} from '@vio/core/src/analytics/types/Events'
import {DatePickerType} from '@vio/core/src/datePicker/types'
import {useToggle} from '@vio/core/src/experiments/hooks/useToggle'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {roomsToConfigurationObject} from '@vio/core/src/room/business/roomConfiguration'
import {REDESIGN_SEARCH_BOX_EXPERIMENT} from '@vio/shared/src/search/constants'
import {guestPickerMessages} from '@vio/shared/src/search/GuestPicker/guestPickerMessages'
import {RedesignedGuestLabel} from '@vio/shared/src/search/GuestPicker/RedesignedGuestLabel'

const SubText = styled(Text)`
  color: ${cssTheme.colors.content.neutral.secondary};
  text-transform: lowercase;
`

const GuestElementText = styled(Text)`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`

const GuestButton = styled(InputButton)`
  min-width: 0;
`

const GuestContent = styled.div`
  min-width: 0;
`

interface Props {
  source: DateGuestPickerSource
}

export const DateGuestInput = ({source}: Props) => {
  const dispatch = useDispatch()
  const dispatchTeamEvent = useDispatchTeamEvent()
  const team = useTrackingTeam()

  const showOverlay = useShowOverlay()
  const showHotelDatePickerOverlay = useShowHotelDatePickerOverlay()

  const isMultiRoomBundlesSearch = useSelector(getIsMultiRoomBundlesSearch)

  const {checkIn = '', checkOut = '', rooms} = useSelector(getSearchBoxValues)
  const isNoDatesSearch = useSelector(getIsNoDatesSearch)
  const isLoading = !checkIn

  const numberOfNights = differenceInDays(parseISO(checkOut), parseISO(checkIn))

  const {numberOfAdults, numberOfChildren, numberOfRooms} =
    roomsToConfigurationObject(rooms)

  const numberOfGuests = numberOfAdults + numberOfChildren

  const toggle = useToggle()
  const isSearchBoxRedesigned = toggle(
    REDESIGN_SEARCH_BOX_EXPERIMENT,
    false,
    true
  )

  const openGuestPicker = useCallback((): void => {
    dispatchTeamEvent(
      trackEvent({
        category: Category.User,
        entity: Entity.GuestPickerInput,
        action: Action.Clicked,
        payload: {
          source
        }
      })
    )

    showOverlay({
      type: OverlayType.HotelGuestPicker,
      entity: Entity.GuestPicker,
      team,
      payload: {source},
      params: {
        source
      }
    })
  }, [team, dispatch, dispatchTeamEvent, source])

  const openDatePicker = useCallback((): void => {
    dispatchTeamEvent(
      trackEvent({
        category: Category.User,
        entity: Entity.DatePickerInput,
        action: Action.Clicked,
        payload: {
          source
        }
      })
    )

    showHotelDatePickerOverlay(DatePickerType.CheckIn, team, source)
  }, [team, dispatch, dispatchTeamEvent, source, showHotelDatePickerOverlay])

  const renderDateContent = () => {
    if (isNoDatesSearch) {
      return (
        <div>
          <Text variant="bodyS">
            <FormattedMessageWrapper
              id="datePicker.selectDates"
              defaultMessage="Select dates"
            />
          </Text>
        </div>
      )
    }

    if (isLoading) {
      return <SkeletonLoader width={56} />
    }

    return (
      <div>
        <Text variant="titleS" tag="p">
          <FormattedDate
            value={dateStringToMiddayDate(checkIn)}
            day="2-digit"
            month="short"
          />
          {' - '}
          <FormattedDate
            value={dateStringToMiddayDate(checkOut)}
            day="2-digit"
            month="short"
          />
        </Text>
        <SubText variant="bodyXS" align="left">
          {checkOut ? (
            <FormattedMessageWrapper
              id="numberOfNights"
              defaultMessage="{count, plural, one {1 night} other {# nights}}"
              values={{count: numberOfNights}}
            />
          ) : (
            <div>&nbsp;</div>
          )}
        </SubText>
      </div>
    )
  }

  const renderGuestContent = () => {
    if (isNoDatesSearch || checkIn) {
      return (
        <GuestContent>
          <GuestElementText variant="titleS" align="left" tag="p">
            {isSearchBoxRedesigned ? (
              <RedesignedGuestLabel
                numberOfAdults={numberOfAdults}
                numberOfChildren={numberOfChildren}
                numberOfRooms={numberOfRooms}
                isRoomsLabelShown={false}
              />
            ) : (
              <FormattedMessageWrapper
                {...guestPickerMessages.guest}
                values={{count: numberOfGuests}}
              />
            )}
          </GuestElementText>
          {!isMultiRoomBundlesSearch && (
            <SubText variant="bodyXS" align="left">
              <FormattedMessageWrapper
                id="numberOfRooms"
                defaultMessage="{count, plural, one {1 room} other {# rooms}}"
                values={{count: numberOfRooms}}
              />
            </SubText>
          )}
        </GuestContent>
      )
    }

    if (isLoading) {
      return <SkeletonLoader width={56} />
    }
  }

  return (
    <Card borderRadius="lg" dataId="dateGuestInput">
      <ButtonGroup>
        <InputButton id="openDatePicker" onClick={openDatePicker}>
          <Icon name="Calendar" colorPath="content.neutral.secondary" />
          {renderDateContent()}
        </InputButton>
        <GuestButton id="openGuestPicker" onClick={openGuestPicker}>
          <Icon name="User" colorPath="content.neutral.secondary" />
          {renderGuestContent()}
        </GuestButton>
      </ButtonGroup>
    </Card>
  )
}
